import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import MDEditor from '@uiw/react-md-editor';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../components/AuthGate';
import { CreateNotificationInput, NotificationsApi, Notification, Recipient } from '../api/notifications';

const EMPTY_FORM = {
  title: '',
  message: '',
};

function defaultScheduleTime(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
}

export default function SendPage() {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [searching, setSearching] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState(defaultScheduleTime);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Notification | null>(null);
  const [error, setError] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await NotificationsApi.searchRecipients(value.trim(), token);
        // Filter out already-selected recipients.
        setSearchResults(results.filter(
          (r) => !selectedRecipients.some((s) => s.recipientId === r.recipientId)
        ));
        setError('');
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: { message?: string } }; status?: number } };
        const msg = axiosErr?.response?.data?.error?.message ?? 'Recipient search failed';
        setError(`Search error (${axiosErr?.response?.status ?? 'network'}): ${msg}`);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [token, selectedRecipients]);

  const handleChange = (field: keyof typeof EMPTY_FORM, value: string) => {
    setSent(null);
    setError('');
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const valid =
    selectedRecipients.length > 0 &&
    form.title.trim() &&
    form.message.trim() &&
    (!schedule || scheduleTime);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    setError('');
    setSent(null);
    try {
      const payload: CreateNotificationInput = {
        recipientIds: selectedRecipients.map((r) => r.recipientId),
        title: form.title,
        message: form.message,
        ...(schedule && scheduleTime
          ? { scheduledFor: new Date(scheduleTime).toISOString() }
          : {}),
      };
      const result = await NotificationsApi.create(payload, token);
      setSent(result[0]);
      setForm(EMPTY_FORM);
      setSelectedRecipients([]);
      setSearchInput('');
      setSearchResults([]);
      setSchedule(false);
      setScheduleTime(defaultScheduleTime());
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string; details?: { fieldErrors?: Record<string, string[]> } } } } };
      const apiError = axiosErr?.response?.data?.error;
      if (apiError?.details?.fieldErrors) {
        const fieldMessages = Object.entries(apiError.details.fieldErrors)
          .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(', ')}`)
          .join(' | ');
        setError(fieldMessages || apiError.message || 'Request failed');
      } else {
        setError(apiError?.message ?? 'Request failed');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Send a notification
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* ── Recipient search ── */}
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={searchResults}
              value={selectedRecipients}
              inputValue={searchInput}
              loading={searching}
              getOptionLabel={(o) => `${o.name} (${o.email})`}
              isOptionEqualToValue={(a, b) => a.recipientId === b.recipientId}
              filterOptions={(x) => x} // server-side search, skip client filter
              noOptionsText={searchInput.trim() ? 'No results found' : 'Type to search…'}
              onChange={(_e, newValue) => {
                setSelectedRecipients(newValue);
                setSearchInput('');
                setSearchResults([]);
              }}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') handleSearchInput(value);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.recipientId}
                    size="small"
                    label={option.name}
                    title={`${option.email} · ${option.recipientId}`}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.recipientId}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recipients"
                  size="small"
                  placeholder={selectedRecipients.length === 0 ? 'Search by name or email…' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Title"
              size="small"
              fullWidth
              required
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              inputProps={{ maxLength: 255 }}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Message
              </Typography>
              <div data-color-mode="light">
                <MDEditor
                  value={form.message}
                  onChange={(val) => handleChange('message', val ?? '')}
                  preview="edit"
                  height={200}
                  textareaProps={{ required: true, placeholder: 'Write your message… (markdown + emoji :rocket: supported)' }}
                />
              </div>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={schedule}
                    onChange={(e) => setSchedule(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" />
                    <Typography variant="body2">Schedule for later</Typography>
                  </Box>
                }
              />
              {schedule && (
                <TextField
                  type="datetime-local"
                  size="small"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  inputProps={{ min: new Date(Date.now() + 60_000).toISOString().slice(0, 16) }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              )}
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {sent && (
              <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />}>
                {sent.scheduledFor
                  ? <>Scheduled for {new Date(sent.scheduledFor).toLocaleString()} — ID: <code>{sent.id}</code></>
                  : <>Sent to {selectedRecipients.length || 1} recipient(s) — ID: <code>{sent.id}</code></>
                }
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={!valid || sending}
              startIcon={sending ? <CircularProgress size={16} /> : schedule ? <ScheduleIcon fontSize="small" /> : <SendIcon fontSize="small" />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {sending ? 'Sending…' : schedule ? 'Schedule notification' : `Send to ${selectedRecipients.length || 0} recipient(s)`}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
